// WhatsApp Chat quick replies (backend/controllers/whatsappQuickReply.js) — shared pieces:
//   QuickReplyPicker      the "/" popup above the chat composer
//   QuickReplyEditDialog  create/edit (chat + WhatsApp Management > Quick Replies tab)
//   QuickReplySendDialog  preview + send a reply that carries photos/videos
// Text-only replies don't need a dialog: picking one fills the composer, staff edit and send.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  MenuItem,
  Tabs,
  Tab,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayCircleOutline as PlayIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import { FONT, TEXT_DARK, TEXT_SUBTLE, BG_TINT, BORDER, PURPLE_MAIN, PURPLE_TINT_100 } from "components/bento/bentoTokens";

export const QR_LANGS = ["en", "th", "lo"];
const LANG_LABEL = { en: "EN", th: "TH", lo: "LO" };
const VARIABLES = [
  { key: "customerName", label: "quickReplies.varCustomerName" },
  { key: "phone", label: "quickReplies.varPhone" },
  { key: "staffName", label: "quickReplies.varStaffName" },
];
const MAX_ATTACHMENTS = 10;
const MAX_FILE_BYTES = 16 * 1024 * 1024;
const MEDIA_ACCEPT = "image/jpeg,image/png,image/gif,image/webp,video/mp4,video/3gpp,video/quicktime";

/** "th-TH" / "lo" / "fr" → one of our languages (en fallback). */
export const toQrLang = (lang) => {
  const l = String(lang || "").slice(0, 2);
  return QR_LANGS.includes(l) ? l : "en";
};

/** The reply's text in `lang`, or the first language that has text. */
export const quickReplyText = (reply, lang) => {
  const own = reply?.text?.[lang];
  if (own && own.trim()) return own;
  return QR_LANGS.map((l) => reply?.text?.[l]).find((s) => s && s.trim()) || "";
};

/** Fill {customerName} / {phone} / {staffName} from the list endpoint's context. */
export const fillQuickReply = (text, context) =>
  String(text || "").replace(/\{(customerName|phone|staffName)\}/g, (_, key) => (context && context[key]) || "");

/** "/" query match: shortcut prefix first, then shortcut/title contains. */
export const filterQuickReplies = (replies, query) => {
  const q = String(query || "").toLowerCase();
  if (!q) return replies;
  const starts = replies.filter((r) => r.shortcut.startsWith(q));
  const contains = replies.filter(
    (r) => !r.shortcut.startsWith(q) && (r.shortcut.includes(q) || (r.title || "").toLowerCase().includes(q))
  );
  return [...starts, ...contains];
};

const MediaThumb = ({ kind, src, size = 56, onRemove }) => (
  <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0, borderRadius: 1, overflow: "hidden", bgcolor: "#000" }}>
    {kind === "video" ? (
      <>
        <Box component="video" src={src} muted preload="metadata" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <PlayIcon sx={{ position: "absolute", inset: 0, m: "auto", color: "#fff", fontSize: size * 0.45 }} />
      </>
    ) : (
      <Box component="img" src={src} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
    )}
    {onRemove && (
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{ position: "absolute", top: 2, right: 2, p: 0.25, bgcolor: "rgba(0,0,0,.55)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,.75)" } }}
      >
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    )}
  </Box>
);

/**
 * The popup over the composer. The parent owns the filtered list + active index (it handles the
 * arrow/Enter/Esc keys in the message box); this only renders.
 */
export const QuickReplyPicker = ({
  replies,
  query,
  activeIndex,
  loading,
  lang,
  onLangChange,
  context,
  canEdit,
  onPick,
  onHover,
  onEdit,
  onNew,
  onClose,
  t,
}) => {
  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-qr-index="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <Paper
      elevation={6}
      sx={{ position: "absolute", left: 12, right: 12, bottom: "100%", mb: 0.5, zIndex: 5, borderRadius: 2, overflow: "hidden", fontFamily: FONT }}
      // Keep focus in the message box while clicking around the popup.
      onMouseDown={(e) => e.preventDefault()}
    >
      <Box sx={{ px: 1.5, py: 0.75, display: "flex", alignItems: "center", gap: 1, borderBottom: `1px solid ${BORDER}`, bgcolor: BG_TINT }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13, color: TEXT_DARK, flex: 1 }}>
          {t("quickReplies.title")}
          {context?.customerName ? (
            <Typography component="span" sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_SUBTLE, ml: 1 }}>
              · {context.customerName}
            </Typography>
          ) : null}
        </Typography>
        {QR_LANGS.map((l) => (
          <Chip
            key={l}
            size="small"
            label={LANG_LABEL[l]}
            color="secondary"
            variant={lang === l ? "filled" : "outlined"}
            onClick={() => onLangChange(l)}
            sx={{ height: 22, fontSize: 11 }}
          />
        ))}
        <Tooltip title={t("quickReplies.new")}>
          <IconButton size="small" color="secondary" onClick={onNew}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label={t("common.close")}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box ref={listRef} sx={{ maxHeight: 280, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={20} color="secondary" />
          </Box>
        ) : replies.length === 0 ? (
          <Typography sx={{ p: 2, fontFamily: FONT, fontSize: 13, color: TEXT_SUBTLE }}>
            {query ? t("quickReplies.noMatch", { query }) : t("quickReplies.empty")}
          </Typography>
        ) : (
          replies.map((r, i) => {
            const preview = fillQuickReply(quickReplyText(r, lang), context);
            return (
              <Box
                key={r._id}
                data-qr-index={i}
                onClick={() => onPick(r)}
                onMouseEnter={() => onHover(i)}
                sx={{
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  bgcolor: i === activeIndex ? PURPLE_TINT_100 : "transparent",
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13, color: PURPLE_MAIN }}>/{r.shortcut}</Typography>
                    {r.title ? (
                      <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_DARK }}>
                        {r.title}
                      </Typography>
                    ) : null}
                    {!r.groupId ? <Chip size="small" variant="outlined" label={t("quickReplies.globalBadge")} sx={{ height: 18, fontSize: 10 }} /> : null}
                    {r.attachments.length ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="secondary"
                        icon={<PhotoLibraryIcon sx={{ fontSize: 12 }} />}
                        label={r.attachments.length}
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    ) : null}
                  </Box>
                  <Typography noWrap sx={{ fontFamily: FONT, fontSize: 12.5, color: TEXT_SUBTLE }}>
                    {preview}
                  </Typography>
                </Box>
                {canEdit(r) && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(r);
                    }}
                    aria-label={t("quickReplies.edit")}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          })
        )}
      </Box>
      <Typography sx={{ px: 1.5, py: 0.5, fontFamily: FONT, fontSize: 11, color: TEXT_SUBTLE, bgcolor: BG_TINT }}>
        {t("quickReplies.hint")}
      </Typography>
    </Paper>
  );
};

const emptyForm = (groupId) => ({ shortcut: "", title: "", text: { en: "", th: "", lo: "" }, sortOrder: 0, groupId: groupId || "" });

/**
 * Create / edit. `onSave(data, files)` and `onDelete(reply)` return { success, message }.
 * `groups` (array of { id, name }) shows the scope picker — WhatsApp Management tab only; in a
 * chat the reply always belongs to that chat's instance.
 */
export const QuickReplyEditDialog = ({ open, reply, defaultGroupId = "", groups = null, onClose, onSave, onDelete, t }) => {
  const [form, setForm] = useState(emptyForm(defaultGroupId));
  const [lang, setLang] = useState("en");
  const [kept, setKept] = useState([]); // existing attachments still on the reply
  const [added, setAdded] = useState([]); // [{ file, url, kind }]
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const textRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(
      reply
        ? { shortcut: reply.shortcut, title: reply.title || "", text: { ...reply.text }, sortOrder: reply.sortOrder || 0, groupId: reply.groupId || "" }
        : emptyForm(defaultGroupId)
    );
    setLang(reply ? QR_LANGS.find((l) => reply.text[l]?.trim()) || "en" : "en");
    setKept(reply ? reply.attachments : []);
    // Previews from the last time the dialog was open are no longer shown.
    setAdded((prev) => {
      prev.forEach((a) => URL.revokeObjectURL(a.url));
      return [];
    });
    setError("");
  }, [open, reply, defaultGroupId]);

  const setText = (value) => setForm((f) => ({ ...f, text: { ...f.text, [lang]: value } }));

  const insertVariable = (key) => {
    const el = textRef.current;
    const current = form.text[lang] || "";
    const token = `{${key}}`;
    const start = el ? el.selectionStart : current.length;
    const end = el ? el.selectionEnd : current.length;
    setText(current.slice(0, start) + token + current.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setError("");
    const next = [];
    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) {
        setError(t("quickReplies.tooLarge", { name: file.name }));
        continue;
      }
      next.push({ file, url: URL.createObjectURL(file), kind: file.type.startsWith("video/") ? "video" : "image" });
    }
    if (kept.length + added.length + next.length > MAX_ATTACHMENTS) {
      next.forEach((a) => URL.revokeObjectURL(a.url));
      setError(t("quickReplies.tooMany", { max: MAX_ATTACHMENTS }));
      return;
    }
    setAdded((prev) => [...prev, ...next]);
  };

  const handleSave = async () => {
    if (!QR_LANGS.some((l) => form.text[l]?.trim()) && kept.length + added.length === 0) {
      setError(t("quickReplies.needContent"));
      return;
    }
    setSaving(true);
    setError("");
    const data = {
      shortcut: form.shortcut,
      title: form.title,
      text: form.text,
      sortOrder: form.sortOrder,
      ...(groups ? { groupId: form.groupId } : {}),
      ...(reply ? { keepAttachmentIds: kept.map((a) => a._id) } : {}),
    };
    const result = await onSave(data, added.map((a) => a.file));
    setSaving(false);
    if (!result.success) setError(result.message);
  };

  const handleDelete = async () => {
    const confirm = await Swal.fire({
      title: t("quickReplies.deleteConfirm", { shortcut: reply.shortcut }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("common.delete"),
      cancelButtonText: t("common.cancel"),
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;
    setSaving(true);
    const result = await onDelete(reply);
    setSaving(false);
    if (!result.success) setError(result.message);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>{reply ? t("quickReplies.edit") : t("quickReplies.new")}</DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label={t("quickReplies.shortcut")}
            value={form.shortcut}
            onChange={(e) => setForm((f) => ({ ...f, shortcut: e.target.value.replace(/[\s/]/g, "").toLowerCase() }))}
            helperText={t("quickReplies.shortcutHelp")}
            InputProps={{ startAdornment: <Typography sx={{ color: TEXT_SUBTLE, mr: 0.25 }}>/</Typography> }}
            inputProps={{ maxLength: 30 }}
            color="secondary"
            size="small"
            sx={{ flex: 1 }}
            autoFocus={!reply}
          />
          <TextField
            label={t("quickReplies.titleLabel")}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            inputProps={{ maxLength: 100 }}
            color="secondary"
            size="small"
            sx={{ flex: 1 }}
          />
        </Box>
        {groups && (
          <TextField
            select
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            label={t("quickReplies.scope")}
            value={form.groupId}
            onChange={(e) => setForm((f) => ({ ...f, groupId: e.target.value }))}
            color="secondary"
            size="small"
          >
            <MenuItem value="">{t("quickReplies.global")}</MenuItem>
            {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Box>
          <Tabs value={lang} onChange={(_, v) => setLang(v)} textColor="secondary" indicatorColor="secondary" sx={{ minHeight: 36 }}>
            {QR_LANGS.map((l) => (
              <Tab
                key={l}
                value={l}
                sx={{ minHeight: 36, py: 0 }}
                label={`${LANG_LABEL[l]}${form.text[l]?.trim() ? " ✓" : ""}`}
              />
            ))}
          </Tabs>
          <TextField
            inputRef={textRef}
            value={form.text[lang] || ""}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("quickReplies.text")}
            helperText={t("quickReplies.textHelp")}
            multiline
            minRows={4}
            maxRows={12}
            fullWidth
            color="secondary"
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.5 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_SUBTLE }}>{t("quickReplies.variables")}:</Typography>
            {VARIABLES.map((v) => (
              <Chip key={v.key} size="small" variant="outlined" color="secondary" label={t(v.label)} onClick={() => insertVariable(v.key)} />
            ))}
          </Box>
        </Box>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 13, color: TEXT_DARK, mb: 1 }}>
            {t("quickReplies.media")}{" "}
            <Typography component="span" sx={{ fontFamily: FONT, fontSize: 12, color: TEXT_SUBTLE }}>
              ({t("quickReplies.mediaLimit", { max: MAX_ATTACHMENTS })})
            </Typography>
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            {kept.map((a) => (
              <MediaThumb key={a._id} kind={a.kind} src={a.url} onRemove={() => setKept((prev) => prev.filter((x) => x._id !== a._id))} />
            ))}
            {added.map((a) => (
              <MediaThumb
                key={a.url}
                kind={a.kind}
                src={a.url}
                onRemove={() => {
                  URL.revokeObjectURL(a.url);
                  setAdded((prev) => prev.filter((x) => x.url !== a.url));
                }}
              />
            ))}
            {kept.length + added.length < MAX_ATTACHMENTS && (
              <Button variant="outlined" color="secondary" startIcon={<AddIcon />} onClick={() => fileRef.current?.click()} sx={{ height: 56 }}>
                {t("quickReplies.addMedia")}
              </Button>
            )}
            <input ref={fileRef} type="file" hidden multiple accept={MEDIA_ACCEPT} onChange={handleFiles} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {reply && onDelete && (
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete} disabled={saving} sx={{ mr: "auto" }}>
            {t("common.delete")}
          </Button>
        )}
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={saving}>
          {t("common.cancel")}
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving || !form.shortcut}>
          {saving ? <CircularProgress size={18} color="inherit" /> : t("quickReplies.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Preview + send for a reply with photos/videos. `onSend(text)` returns { success, message }. */
export const QuickReplySendDialog = ({ open, reply, initialText, onCancel, onSend, t }) => {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setText(initialText || "");
      setError("");
    }
  }, [open, initialText]);

  const attachments = useMemo(() => reply?.attachments || [], [reply]);

  const handleSend = async () => {
    setSending(true);
    setError("");
    const result = await onSend(text);
    setSending(false);
    if (!result.success) setError(result.message);
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>
        {reply ? t("quickReplies.sendTitle", { shortcut: reply.shortcut }) : ""}
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {attachments.map((a) => (
            <MediaThumb key={a._id} kind={a.kind} src={a.url} size={96} />
          ))}
        </Box>
        <TextField
          label={t("quickReplies.sendCaption")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={3}
          maxRows={10}
          fullWidth
          color="secondary"
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" color="secondary" onClick={onCancel} disabled={sending}>
          {t("common.cancel")}
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSend} disabled={sending}>
          {sending ? <CircularProgress size={18} color="inherit" /> : t("quickReplies.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
